import React, { useEffect, useMemo, useState } from 'react';
import useStyles from './style';
import { Box, Paper, Typography, Button } from '@mui/material';
import SearchBar from '../../components/SearchBar';
import NewFilterDrawer from '../../components/shared/FilterDrawer/NewFilterDrawer';
import IndependantRepTable from '../ManageIndepenantRep/IndependantRepTable';
import FILTER_CONSTANTS, { FILTER_INDEPENDENT_REP_ROLE, FILTER_VALUES } from '../../constants/FilterConstants';
import { GridContainer, GridItem, Breadcrumbs } from '../../components/shared';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchFilterParam } from '../../hooks';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../constants/pagination';
import { useLazyGetAllSublocationsQuery } from '../../services/Sublocation/SublocationService';
import { useLazyGetUsersForSelectQuery } from '../../services/Users/UsersService';
import { useGetIndependentRepsQuery } from '../../services/RepRate/RepRateService'; // Import the API hook
import { resetIRUsersFiltersState, setIReUsersFilters } from '../../redux-slice/IndependantRepPayFilters';
import { useNavigate } from 'react-router-dom';
import { routeConstants } from '../../constants/routeConstants';
import BREADCRUMB_NAMES from '../../constants/breadcrumbNames';
const displayName = '';
const IndependantRep = () => {
  const [searchText, setSearchText] = useState('');
  const classes = useStyles();
  const { DRAWER_TYPES, TITLE, FILTER_DATA } = FILTER_CONSTANTS;
  const { RepPay = {} } = useSelector((state) => state.repPayFilter);
  const [filterParams, setFilterParams] = useState(FILTER_DATA.REPPAY.INITIAL_PARAMS);
  const [getAllSublocations] = useLazyGetAllSublocationsQuery();
  const [getIndependentRepUsers] = useLazyGetUsersForSelectQuery();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(RepPay?.pageNumber ? RepPay?.pageNumber : DEFAULT_PAGE);
  const [perPageNumber, setPerPageNumber] = useState(DEFAULT_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();

  const getCustomParams = (value) => {
    switch (value) {
      case FILTER_VALUES.INDEPENDENTREP:
        return { api: getIndependentRepUsers, customParam: `role=${FILTER_INDEPENDENT_REP_ROLE}` };
      case FILTER_VALUES.SUBLOCATION:
        return { api: getAllSublocations, customParam: 'status=true' };
      default:
        return {};
    }
  };

  const filterData = useMemo(
    () =>
      FILTER_DATA.REPPAY.FILTER.map((item) => {
        const api = getCustomParams(item.value);
        return {
          ...item,
          ...api,
        };
      }),
    [],
  );

  const searchParams = useSearchFilterParam({
    filterKey: FILTER_DATA.REPPAY,
    filterParams,
    searchText,
  });

  // Call the API using the hook
  const {
    currentData: latestData,
    isError,
    isSuccess,
    isLoading,
    isFetching,
  } = useGetIndependentRepsQuery({ searchParams, page: currentPage, perPage: perPageNumber }, { skip: false });

  // Process API data
  useEffect(() => {
    if (isSuccess && latestData) {
      setTotalCount(latestData?.pagination?.total || 0);
    } else if (isError) {
      setTotalCount(0);
    }
  }, [isSuccess, isError, latestData]);

  useEffect(() => {
    const storedFilterParams = {
      [FILTER_VALUES.INDEPENDENTREP]: [],
      [FILTER_VALUES.SUBLOCATION]: [],
    };

    if (RepPay[FILTER_VALUES.INDEPENDENTREP] && RepPay[FILTER_VALUES.INDEPENDENTREP]?.length > 0) {
      storedFilterParams.independentRepId = RepPay[FILTER_VALUES.INDEPENDENTREP];
    }
    if (RepPay[FILTER_VALUES.SUBLOCATION] && RepPay[FILTER_VALUES.SUBLOCATION]?.length > 0) {
      storedFilterParams.subLocationId = RepPay[FILTER_VALUES.SUBLOCATION];
    }
    setSearchText(RepPay.searchText);
    setFilterParams(storedFilterParams);
  }, [RepPay]);

  const searchHandler = (params) => {
    setCurrentPage(DEFAULT_PAGE);
    setPerPageNumber(DEFAULT_PER_PAGE);
    dispatch(
      setIReUsersFilters({
        ...RepPay,
        searchText: params,
        pageNumber: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_PER_PAGE,
      }),
    );
    setSearchText(params);
  };

  const resetFilterParams = () => {
    setFilterParams(FILTER_DATA.REPPAY.INITIAL_PARAMS);
    dispatch(resetIRUsersFiltersState());
  };

  const onApplyFilterHandler = (v) => {
    dispatch(setIReUsersFilters({ ...RepPay, ...v }));
    setFilterParams(v);
  };

  const onReset = () => {
    dispatch(
      setIReUsersFilters({
        ...RepPay,
        searchText: '',
        pageNumber: DEFAULT_PAGE,
        rowsPerPage: DEFAULT_PER_PAGE,
      }),
    );
    setSearchText('');
  };

  // Pagination handlers
  const pageChangeHandler = (newPage) => {
    setCurrentPage(newPage);
    dispatch(
      setIReUsersFilters({
        ...RepPay,
        pageNumber: newPage,
      }),
    );
  };

  const perPageChangeHandler = (newPerPage) => {
    setPerPageNumber(newPerPage);
    dispatch(
      setIReUsersFilters({
        ...RepPay,
        rowsPerPage: newPerPage,
        pageNumber: currentPage,
      }),
    );
  };

  // Breadcrumb data
  const {
    SETTINGS: { MANAGE_INDEPENDENT_REP, SETTINGS },
  } = BREADCRUMB_NAMES;

  const breadcrumbData = useMemo(
    () => [
      { text: SETTINGS, redirection: routeConstants.SETTING_ROUTE },
      { text: MANAGE_INDEPENDENT_REP, redirection: routeConstants.INDEPENDANT_REP },
    ],
    [],
  );

  return (
    <React.Fragment>
      <Box mb={2}>
        <Breadcrumbs variant="body2" underline="hover" data={breadcrumbData} />
      </Box>
      <GridContainer className={classes.pageName} justifyContent={'space-between'}>
        <GridItem xs={12}>
          <Typography variant="h1">{'Manage Independent Rep'}</Typography>
        </GridItem>
      </GridContainer>
      <Box component={Paper}>
        <Box display={'flex'} marginBottom={1} padding={2} gap={2}>
          <Box width={'100%'}>
            <SearchBar onSearch={searchHandler} placeholder={'Search'} searchText={searchText} onReset={onReset} />
          </Box>
          <Box pl={3}>
            <NewFilterDrawer
              filteredValues={filterParams}
              name={TITLE.FILTERS.toUpperCase()}
              title={TITLE.FILTERS}
              drawerType={DRAWER_TYPES.FILTER_DRAWER}
              onReset={resetFilterParams}
              onApplyFilter={onApplyFilterHandler}
              filterData={filterData}
              emptyFilterLength={
                FILTER_DATA.REPPAY.INITIAL_PARAMS ? JSON.stringify(FILTER_DATA.REPPAY.INITIAL_PARAMS)?.length : 0
              }
            />
          </Box>
          <Box pl={3}>
            <Button
              variant="contained"
              color="primary"
              sx={{
                height: '100%',
                whiteSpace: 'nowrap',
              }}
              onClick={() => navigate(routeConstants.ASSIGN_INDEPENDANT_REP)}
            >
              +ADD NEW
            </Button>
          </Box>
        </Box>
        <IndependantRepTable
          apidata={latestData}
          isSuccess={isSuccess}
          isLoading={isLoading}
          isError={isError}
          isFetching={isFetching}
          currentPage={currentPage}
          perPageNumber={perPageNumber}
          totalCount={totalCount}
          pageChangeHandler={pageChangeHandler}
          perPageChangeHandler={perPageChangeHandler}
        />
      </Box>
    </React.Fragment>
  );
};

IndependantRep.displayName = displayName;
export default IndependantRep;
